import { Box, CircularProgress, Grid } from '@material-ui/core';
import { useNavigate } from 'react-router';

import { InclusionFilter } from '@/components/modules/PricingsList/_shared/constants/InclusionFilterSelectOptions';
import {
  ALL_PAYMENT_METHODS,
  PricingsFormSchema,
} from '@/components/modules/PricingsList/_shared/schemas/pricingsForm.schema';
import { Filters } from '@/components/modules/PricingsList/components/Filters/Filters';
import { PricingsListTable } from '@/components/modules/PricingsList/components/PricingsTable/PricingsListTable';
import { usePricingsList } from '@/components/modules/PricingsList/hooks/usePricingsList';
import { Font } from '@/components/shared/components/Font';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { ModuleCorePermissionProps } from '@/config/types';
import { ALL_LENDERS } from '@/constants/AllLenders';
import { useUser } from '@/hooks/useUser';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

const PricingsList = ({ canEdit = false }: ModuleCorePermissionProps) => {
  const { pricingsListEntity, page, setPage, refreshTable, loading } = usePricingsList();
  const { userEntity } = useUser();

  const navigate = useNavigate();

  const { getTextIdTranslation } = useTranslation();

  const actions: ActionsOption[] = [
    ...(canEdit
      ? [
          {
            iconName: 'add',
            display: getTextIdTranslation('CREATE_NEW_PRICING') as string,
            onClick: () => navigate('/pricing/new'),
          },
        ]
      : []),
    ...(!loading
      ? [
          {
            iconName: 'refresh',
            display: getTextIdTranslation('RELOAD_PRICINGS') as string,
            onClick: () => refreshTable(),
          },
        ]
      : []),
  ];

  if (!userEntity) {
    return null;
  }

  const pricings = pricingsListEntity?.getFilteredPricings();

  return (
    <SubSectionWrapper specificActionsOptionsArray={actions}>
      {loading || !pricings ? (
        <Box
          pt={3}
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          justifyContent={'center'}
          width={'100%'}
        >
          <CircularProgress color={'secondary'} size={48} thickness={3} />
          <Font variant={'subtitle2'} color={'textSecondary'} style={{ paddingTop: 24 }}>
            <Text tId={'LOADING_PRICINGS'} />
          </Font>
        </Box>
      ) : (
        <FormWrapper
          formSchema={() => PricingsFormSchema}
          defaultValues={{
            paymentMethod:
              userEntity?.allowedPaymentMethodTypes && userEntity.allowedPaymentMethodTypes.length > 1
                ? ALL_PAYMENT_METHODS
                : userEntity?.allowedPaymentMethodTypes[0] ?? null,
            insurances: [],
            gracePeriod: InclusionFilter.ALL,
            setupFee: InclusionFilter.ALL,
          }}
        >
          <Grid className="appear-anim" container spacing={4}>
            <Grid item xs={12} md={3}>
              <Filters setPage={setPage} />
            </Grid>
            <Grid item xs={12} md={9}>
              <PricingsListTable
                pricings={pricings}
                refreshData={refreshTable}
                lenders={ALL_LENDERS}
                page={page}
                setPage={setPage}
              />
            </Grid>
          </Grid>
        </FormWrapper>
      )}
    </SubSectionWrapper>
  );
};
export const PricingsListModule = withPermissionsModule(PricingsList);
